import { useState, useCallback, useRef, useContext } from 'react';
import { flushSync } from 'react-dom';
import { SALES_AI_ENDPOINT } from '@/lib/sales-chat/constants';
import type { AIMessage, AIToolCall, AIToolResult, AIQuestion, StreamEvent, AIShowUser } from '../ai-chat/types';
import { useSalesAIChatContext } from './SalesAIChatContext';

// Simplified response type for sales AI
interface SalesAIResponse {
  response: string;
  toolCalls?: AIToolCall[];
  questions?: AIQuestion[];
  showUser?: AIShowUser;
  usage?: {
    durationMs: number;
  };
  error?: string;
}

// Helper to generate simple session ID
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

export const useSalesAIChat = () => {
  // Try to use shared context first (for landing page where multiple instances need to share state)
  // Falls back to local state if context is not available (for backward compatibility)
  const contextState = useSalesAIChatContext();

  if (contextState) {
    // Using shared context - return the context values directly
    return contextState;
  }

  // Local state fallback (for when used without SalesAIChatProvider)
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>(generateSessionId());

  // Process SSE streaming response
  const processStreamingResponse = useCallback(async (
    response: Response
  ): Promise<{ content: string; toolCalls: AIToolCall[]; questions: AIQuestion[]; showUser?: AIShowUser }> => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let content = '';
    let toolCalls: AIToolCall[] = [];
    let questions: AIQuestion[] = [];
    let showUser: AIShowUser | undefined = undefined;
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event: StreamEvent = JSON.parse(data);

            switch (event.type) {
              case 'content':
                if (event.content) {
                  content += event.content;
                  flushSync(() => {
                    setStreamingContent(content);
                  });
                }
                break;

              case 'done':
                if (event.response) content = event.response;
                if (event.toolCalls) toolCalls = event.toolCalls;
                if (event.questions) questions = event.questions;
                if (event.showUser) showUser = event.showUser;
                break;

              case 'error':
                throw new Error(event.error || 'Streaming error');
            }
          } catch {
            // Ignore parse errors
          }
        }
      }

      // Process remaining buffer
      if (buffer.startsWith('data: ')) {
        const data = buffer.slice(6);
        if (data && data !== '[DONE]') {
          try {
            const event: StreamEvent = JSON.parse(data);
            if (event.type === 'done') {
              if (event.response) content = event.response;
              if (event.toolCalls) toolCalls = event.toolCalls;
              if (event.questions) questions = event.questions;
              if (event.showUser) showUser = event.showUser;
            }
          } catch {
            // Ignore
          }
        }
      }
    } finally {
      reader.releaseLock();
      setStreamingContent('');
    }

    return { content, toolCalls, questions, showUser };
  }, []);

  // Internal function to execute the API call and handle response
  // This is used by both sendMessage and resendMessage
  const executeApiCall = useCallback(async (
    userMessageContent: string,
    currentMessages: AIMessage[]
  ): Promise<{ content: string; toolCalls?: AIToolCall[]; questions?: AIQuestion[]; showUser?: AIShowUser }> => {
    // Create abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const response = await fetch(SALES_AI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: userMessageContent,
        conversationHistory: currentMessages.slice(-10),
        sessionId: sessionIdRef.current,
        stream: true,
      }),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    let data: SalesAIResponse;
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('text/event-stream')) {
      const streamResult = await processStreamingResponse(response);
      data = {
        response: streamResult.content,
        toolCalls: streamResult.toolCalls.length > 0 ? streamResult.toolCalls : undefined,
        questions: streamResult.questions.length > 0 ? streamResult.questions : undefined,
        showUser: streamResult.showUser,
      };
    } else {
      data = await response.json();
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    // NOTE: We don't auto-dispatch show_user events anymore.
    // The button in AIMessage will handle all navigation (scroll and routes) on user click.

    return {
      content: data?.response || "I'm here to help you learn about TagInn. What would you like to know?",
      toolCalls: data?.toolCalls,
      questions: data?.questions,
      showUser: data?.showUser,
    };
  }, [processStreamingResponse]);

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    const trimmedContent = content.trim();
    if (!trimmedContent) return;

    if (trimmedContent.length > 2000) {
      setError('Message too long. Please keep it under 2000 characters.');
      return;
    }

    // Create user message
    const userMessageId = crypto.randomUUID();
    const userMessage: AIMessage = {
      id: userMessageId,
      role: 'user',
      content: trimmedContent,
      timestamp: new Date().toISOString(),
    };

    // Add user message to state
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const result = await executeApiCall(trimmedContent, [...messages, userMessage]);

      // Create assistant message
      const assistantMessageId = crypto.randomUUID();
      const assistantMessage: AIMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: result.content,
        timestamp: new Date().toISOString(),
        toolCalls: result.toolCalls,
        questions: result.questions,
        showUser: result.showUser,
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      // Check if aborted
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request stopped');
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);

      // Add error message
      const errorMessageId = crypto.randomUUID();
      const errorResponse: AIMessage = {
        id: errorMessageId,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, executeApiCall]);

  // Stop generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  // Clear conversation (start fresh)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    sessionIdRef.current = generateSessionId();
  }, []);

  // Resend a message - re-runs the same turn without adding a duplicate message
  // For sales mode, we don't branch, just re-run the API call
  const resendMessage = useCallback(async (message: AIMessage, newContent?: string) => {
    const messageIndex = messages.findIndex(m => m.id === message.id);
    if (messageIndex === -1) {
      console.warn('[Sales AI] Message not found for resend:', message.id);
      return;
    }

    // Only user messages can be resent
    if (message.role !== 'user') {
      console.warn('[Sales AI] Only user messages can be resent');
      return;
    }

    // Clear all messages after this message (including any assistant response)
    const messagesToKeep = messages.slice(0, messageIndex + 1);

    // Update the message content if provided
    const contentToSend = newContent || message.content;
    const updatedMessages = newContent && newContent !== message.content
      ? messagesToKeep.map(m => m.id === message.id ? { ...m, content: newContent } : m)
      : messagesToKeep;

    setMessages(updatedMessages);
    setIsLoading(true);
    setError(null);

    try {
      const result = await executeApiCall(contentToSend, updatedMessages);

      // Create assistant message
      const assistantMessageId = crypto.randomUUID();
      const assistantMessage: AIMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: result.content,
        timestamp: new Date().toISOString(),
        toolCalls: result.toolCalls,
        questions: result.questions,
        showUser: result.showUser,
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      // Check if aborted
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request stopped');
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);

      // Add error message
      const errorMessageId = crypto.randomUUID();
      const errorResponse: AIMessage = {
        id: errorMessageId,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, executeApiCall]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    stopGeneration,
    clearMessages,
    streamingContent,
    resendMessage,
  };
};

export default useSalesAIChat;
